import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import classNames from 'classnames';
import VendorStatementIdInput from '../VendorStatementIdInput/VendorStatementIdInput';
import VendorStatementEmailInput from '../VendorStatementEmailInput/VendorStatementEmailInput';
import './VendorStatementFormModal.scss';
import { IconPaperPlane, IconSuccessRounded } from '../../../components/Icons';

export type VendorStatementFormModalState = 'form' | 'sending' | 'success';

export interface EmailRecipient {
  email: string;
  isValid: boolean;
  isConfirmed?: boolean;
  isSelected: boolean;
}

export interface VendorStatementFormData {
  id: string;
  replyTo: string;
  sendTo: EmailRecipient[];
  newEmail?: string;
}

export interface VendorStatementFormModalProps {
  /** Current state of the modal */
  state?: VendorStatementFormModalState;
  /** Whether modal is open */
  isOpen?: boolean;
  /** Initial form data */
  initialData?: Partial<VendorStatementFormData>;
  /** Position for the modal */
  position?: { top: number; left: number } | null;
  /** Whether emails are loading */
  isLoadingEmails?: boolean;
  /** Whether form is sending */
  isSending?: boolean;
  /** Vendor name to display in modal header */
  vendorName?: string;
  /** Callback when modal should close */
  onClose?: () => void;
  /** Callback when form is submitted */
  onSubmit?: (data: VendorStatementFormData) => Promise<void>;
  /** Callback when state changes */
  onStateChange?: (state: VendorStatementFormModalState) => void;
}

/**
 * VendorStatementFormModal component for sending vendor statement requests
 *
 * Features:
 * - Form state with ID input, reply-to field, and email management
 * - Sending state with loading animation
 * - Success state with confirmation message
 * - Integration with react-hook-form for form management
 * - Dynamic email list management with validation
 */
const VendorStatementFormModal: React.FC<VendorStatementFormModalProps> = ({
  state = 'form',
  isOpen = false,
  initialData,
  position = null,
  isLoadingEmails = false,
  isSending = false,
  vendorName,
  onClose,
  onSubmit,
  onStateChange,
}) => {
  const [currentState, setCurrentState] = useState(state);
  const [emailList, setEmailList] = useState<EmailRecipient[]>([]);
  const [newEmailState, setNewEmailState] = useState<'placeholder' | 'error' | 'valid' | 'validating' | 'success'>(
    'placeholder'
  );
  const [showNewEmailInput, setShowNewEmailInput] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<VendorStatementFormData>({
    defaultValues: {
      id: '',
      replyTo: initialData?.replyTo,
      sendTo: initialData?.sendTo || emailList,
      newEmail: '',
    },
  });

  const watchedId = watch('id');
  const watchedReplyTo = watch('replyTo');
  const watchedNewEmail = watch('newEmail');

  useEffect(() => {
    setCurrentState(state);
  }, [state]);

  useEffect(() => {
    if (isOpen && initialData?.sendTo) {
      setEmailList(initialData.sendTo);
      setValue('sendTo', initialData.sendTo);
    } else if (isOpen) {
      // Reset email list when opening without sendTo data
      setEmailList([]);
      setValue('sendTo', []);
    }
  }, [isOpen, initialData?.sendTo, setValue]);

  useEffect(() => {
    if (isOpen && initialData?.id && initialData.id !== 'Account Number') {
      setValue('id', initialData.id);
    } else if (isOpen) {
      // Reset ID to placeholder when opening without ID data
      setValue('id', '');
    }
  }, [isOpen, initialData?.id, setValue]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      reset({
        id: initialData?.id || '',
        replyTo: initialData?.replyTo,
        sendTo: initialData?.sendTo || [],
        newEmail: '',
      });
      setCurrentState('form');
      setShowNewEmailInput(false);
      setNewEmailState('placeholder');
    }
  }, [isOpen, reset, initialData]);

  const handleStateChange = (newState: VendorStatementFormModalState) => {
    setCurrentState(newState);
    onStateChange?.(newState);
  };

  const handleAddEmail = () => {
    setShowNewEmailInput(true);
  };

  const handleNewEmailChange = (value: string) => {
    setValue('newEmail', value);

    // Update email validation state
    if (value === '') {
      setNewEmailState('placeholder');
    } else if (value.includes('@') && value.includes('.') && value.length > 5) {
      setNewEmailState('validating');
      // Simulate validation
      setTimeout(() => {
        setNewEmailState('success');
      }, 1000);
    } else {
      setNewEmailState('error');
    }
  };

  const handleConfirmNewEmail = () => {
    if (watchedNewEmail && newEmailState === 'success') {
      const newRecipient: EmailRecipient = {
        email: watchedNewEmail,
        isValid: true,
        isConfirmed: true,
        isSelected: true,
      };

      const updatedList = [...emailList, newRecipient];
      setEmailList(updatedList);
      setValue('sendTo', updatedList);
      setValue('newEmail', '');
      setShowNewEmailInput(false);
      setNewEmailState('placeholder');
    }
  };

  const handleToggleEmail = (index: number) => {
    const updatedList = emailList.map((recipient, i) =>
      i === index ? { ...recipient, isSelected: !recipient.isSelected } : recipient
    );
    setEmailList(updatedList);
    setValue('sendTo', updatedList);
  };

  const onFormSubmit = async (data: VendorStatementFormData) => {
    try {
      handleStateChange('sending');

      if (onSubmit) {
        await onSubmit(data);
      } else {
        console.warn('No onSubmit handler provided');
      }

      setTimeout(() => {
        handleStateChange('success');
      }, 2000);
    } catch (error) {
      console.error('Error in form submission:', error);
      handleStateChange('form');
    }
  };

  const handleClose = () => {
    reset();
    setEmailList([]);
    setShowNewEmailInput(false);
    setNewEmailState('placeholder');
    setCurrentState('form');
    onClose?.();
  };

  const selectedEmailsCount = emailList.filter(email => email.isSelected).length;

  const renderModalContent = () => {
    switch (currentState) {
      case 'form':
        return (
          <>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <polyline
                      points="22,6 12,13 2,6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h2 className="modal-title">Send Request to{vendorName ? ` ${vendorName}` : ''}</h2>
              </div>
              <button className="modal-close" onClick={handleClose}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit(onFormSubmit)} className="modal-form">
              <div className="form-legend">
                <span className="legend-text">
                  Fields marked with <span className="required-asterisk">*</span> are required
                </span>
              </div>

              <div className="form-section">
                <div className="required-field-wrapper">
                  <Controller
                    name="id"
                    control={control}
                    rules={{ required: 'Account ID is required' }}
                    render={({ field }) => (
                      <VendorStatementIdInput
                        label="ID"
                        value={isLoadingEmails && watchedId === 'Account Number' ? 'Loading...' : field.value}
                        onValueChange={field.onChange}
                        state={isLoadingEmails && watchedId === 'Account Number' ? 'loading' : 'display'}
                        isLoading={isLoadingEmails && watchedId === 'Account Number'}
                      />
                    )}
                  />
                </div>
                {errors.id && <div className="field-error">{errors.id.message}</div>}
              </div>

              <div className="form-section">
                <Controller
                  name="replyTo"
                  control={control}
                  render={({ field }) => (
                    <VendorStatementEmailInput
                      label="Reply To"
                      variant="reply-to"
                      value={field.value}
                      onValueChange={field.onChange}
                      state={watchedReplyTo === 'Email' ? 'placeholder' : 'display'}
                    />
                  )}
                />
              </div>

              <div className="form-section">
                <div className="section-header">
                  <span className="section-label">
                    Send To<span className="required-asterisk"> *</span>
                  </span>
                  <button type="button" className="add-email-button" onClick={handleAddEmail}>
                    ADD EMAIL
                  </button>
                </div>

                <div className="email-list">
                  {isLoadingEmails && (
                    <div className="loading-emails">
                      <span>Loading vendor emails...</span>
                    </div>
                  )}
                  {!isLoadingEmails && emailList.length === 0 && !showNewEmailInput && (
                    <div className="no-emails-message">
                      <span>No contact email found in QBO, add one using the button above</span>
                    </div>
                  )}
                  {emailList.map((recipient, index) => (
                    <div key={index} className={classNames('email-item', { selected: recipient.isSelected })}>
                      <span className="email-text">{recipient.email}</span>
                      <div className="email-actions">
                        <button
                          type="button"
                          className={classNames('email-checkbox', { checked: recipient.isSelected })}
                          onClick={() => handleToggleEmail(index)}
                        >
                          {recipient.isSelected && (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                              <path
                                d="M10 3L4.5 8.5L2 6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}

                  {showNewEmailInput && (
                    <div className="new-email-input">
                      <VendorStatementEmailInput
                        state={newEmailState}
                        value={watchedNewEmail}
                        onValueChange={handleNewEmailChange}
                        placeholder="enter new email address"
                        label=""
                        autoFocus={true}
                      />
                      {newEmailState === 'success' && (
                        <button type="button" className="confirm-email" onClick={handleConfirmNewEmail}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                              d="M13.5 4.5L6 12L2.5 8.5"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="submit"
                  className="send-button"
                  disabled={isSubmitting || selectedEmailsCount === 0 || isSending || Object.keys(errors).length > 0}
                >
                  {isSending ? 'SENDING...' : selectedEmailsCount > 0 ? `SEND (${selectedEmailsCount})` : 'SEND'}
                </button>
              </div>
            </form>
          </>
        );

      case 'sending':
        return (
          <>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <polyline
                      points="22,6 12,13 2,6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h2 className="modal-title">Sending requests{vendorName ? ` to ${vendorName}` : ''} ....</h2>
              </div>
              <button className="modal-close" onClick={handleClose}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="modal-sending">
              <div className="sending-icon">
                <IconPaperPlane />
              </div>
              <h3 className="sending-title">Sending request assessments</h3>
            </div>
          </>
        );

      case 'success':
        return (
          <>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <polyline
                      points="22,6 12,13 2,6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h2 className="modal-title">Requests sent{vendorName ? ` to ${vendorName}` : ''}</h2>
              </div>
              <button className="modal-close" onClick={handleClose}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="modal-success">
              <div className="success-icon">
                <IconSuccessRounded />
              </div>
              <h3 className="success-title">Requests sent</h3>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  // If position is provided, use absolute positioning
  const modalStyle = position
    ? {
        position: 'fixed' as const,
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 10000,
      }
    : {};

  const overlayStyle = position
    ? {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'transparent',
        zIndex: 9999,
      }
    : {};

  const modalContent = (
    <div className="vendor-statement-form-modal-overlay" onClick={handleClose} style={position ? overlayStyle : {}}>
      <div
        className={classNames('vendor-statement-form-modal', `state-${currentState}`, {
          'positioned-modal': !!position,
        })}
        onClick={e => e.stopPropagation()}
        style={modalStyle}
      >
        {renderModalContent()}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default VendorStatementFormModal;
